import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
    Plus,
    Search,
    HelpCircle,
    Edit2,
    Trash2,
    GripVertical,
    ChevronUp,
    ChevronDown,
    Save,
    X
} from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from '../ui/dialog';
import { ConfirmDialog } from '../ui/confirm-dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../ui/select';

interface Faq {
    _id: string;
    question: string;
    answer: string;
    category: string;
    order: number;
    styling?: {
        italic?: boolean;
        bold?: boolean;
    };
}

export function FaqManagement() {
    const [faqs, setFaqs] = useState<Faq[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingFaq, setEditingFaq] = useState<Faq | null>(null);
    const [formData, setFormData] = useState({
        question: '',
        answer: '',
        category: 'General',
        order: 0,
        styling: {
            italic: false,
            bold: false
        }
    });
    const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string | null }>({ open: false, id: null });
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchFaqs = async () => {
        try {
            setLoading(true);
            const data = await apiClient.getFaqs();
            setFaqs(data || []);
        } catch (error) {
            toast.error('Failed to load FAQs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFaqs();
    }, []);

    const handleOpenAdd = () => {
        setEditingFaq(null);
        setFormData({
            question: '',
            answer: '',
            category: 'General',
            order: faqs.length,
            styling: {
                italic: false,
                bold: false
            }
        });
        setIsDialogOpen(true);
    };

    const handleEdit = (faq: Faq) => {
        setEditingFaq(faq);
        setFormData({
            question: faq.question,
            answer: faq.answer,
            category: faq.category,
            order: faq.order,
            styling: {
                italic: faq.styling?.italic || false,
                bold: faq.styling?.bold || false
            }
        });
        setIsDialogOpen(true);
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingFaq) {
                await apiClient.updateFaq(editingFaq._id, formData);
                toast.success('FAQ updated successfully');
            } else {
                await apiClient.createFaq(formData);
                toast.success('FAQ created successfully');
            }
            setIsDialogOpen(false);
            fetchFaqs();
        } catch (error) {
            toast.error('Failed to save FAQ');
        }
    };

    const handleDeleteClick = (id: string) => {
        setDeleteDialog({ open: true, id });
    };

    const handleConfirmDelete = async () => {
        if (!deleteDialog.id) return;
        setIsDeleting(true);
        try {
            await apiClient.deleteFaq(deleteDialog.id);
            toast.success('FAQ deleted successfully');
            fetchFaqs();
            setDeleteDialog({ open: false, id: null });
        } catch (error) {
            toast.error('Failed to delete FAQ');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleMove = async (index: number, direction: 'up' | 'down') => {
        const newFaqs = [...faqs];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= newFaqs.length) return;

        // Swap
        [newFaqs[index], newFaqs[targetIndex]] = [newFaqs[targetIndex], newFaqs[index]];

        // Update orders
        const updatedFaqs = newFaqs.map((f, i) => ({ ...f, order: i }));
        setFaqs(updatedFaqs);

        try {
            await apiClient.bulkUpdateFaqs(updatedFaqs);
        } catch (error) {
            toast.error('Failed to update FAQ order');
            fetchFaqs();
        }
    };

    const filteredFaqs = faqs.filter(f =>
        f.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.category.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold">FAQ Management</h2>
                    <p className="text-gray-600">Add, edit, and organize frequently asked questions</p>
                </div>
                <Button onClick={handleOpenAdd}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add FAQ
                </Button>
            </div>

            <Card>
                <CardHeader className="pb-3 border-b">
                    <div className="flex items-center gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <Input
                                placeholder="Search FAQs..."
                                className="pl-10"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-12 text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                            <p>Loading FAQs...</p>
                        </div>
                    ) : filteredFaqs.length > 0 ? (
                        <div className="divide-y">
                            {filteredFaqs.map((faq, index) => (
                                <div key={faq._id} className="p-3 hover:bg-gray-50 flex gap-3 items-start">
                                    <div className="flex flex-col gap-0.5">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 text-gray-400"
                                            disabled={index === 0}
                                            onClick={() => handleMove(index, 'up')}
                                        >
                                            <ChevronUp className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 text-gray-400"
                                            disabled={index === filteredFaqs.length - 1}
                                            onClick={() => handleMove(index, 'down')}
                                        >
                                            <ChevronDown className="h-4 w-4" />
                                        </Button>
                                    </div>

                                    <div className="flex-1">
                                        <div className="flex justify-between items-center mb-1">
                                            <div className="flex items-center gap-2">
                                                <HelpCircle className="w-3.5 h-3.5 text-blue-600" />
                                                <h4 className="font-semibold text-base leading-tight">{faq.question}</h4>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Badge variant="outline" className="text-[10px] px-1.5 h-5">{faq.category}</Badge>
                                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(faq)}>
                                                    <Edit2 className="w-3.5 h-3.5 text-gray-500" />
                                                </Button>
                                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteClick(faq._id)}>
                                                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                                </Button>
                                            </div>
                                        </div>
                                        <p className={`text-gray-500 text-xs whitespace-pre-wrap leading-relaxed ${faq.styling?.italic ? 'italic' : ''} ${faq.styling?.bold ? 'font-bold' : ''}`}>
                                            {faq.answer.length > 200 ? faq.answer.substring(0, 200) + '...' : faq.answer}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-12 text-center">
                            <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500">No FAQs found matching your search</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{editingFaq ? 'Edit FAQ' : 'Add New FAQ'}</DialogTitle>
                        <DialogDescription>
                            Provide clear and concise information for your users.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSave} className="space-y-4 pt-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Category</label>
                                <Select
                                    value={formData.category}
                                    onValueChange={(v) => setFormData({ ...formData, category: v })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select category" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="General">General</SelectItem>
                                        <SelectItem value="Accounts">Accounts</SelectItem>
                                        <SelectItem value="Job Seekers">Job Seekers</SelectItem>
                                        <SelectItem value="Employers">Employers</SelectItem>
                                        <SelectItem value="Payments">Payments</SelectItem>
                                        <SelectItem value="Technical">Technical</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Text Styling</label>
                                <div className="flex gap-2">
                                    <Button
                                        type="button"
                                        variant={formData.styling.bold ? "default" : "outline"}
                                        className="w-10 h-10 p-0"
                                        onClick={() => setFormData({
                                            ...formData,
                                            styling: { ...formData.styling, bold: !formData.styling.bold }
                                        })}
                                    >
                                        <span className="font-bold">B</span>
                                    </Button>
                                    <Button
                                        type="button"
                                        variant={formData.styling.italic ? "default" : "outline"}
                                        className="w-10 h-10 p-0"
                                        onClick={() => setFormData({
                                            ...formData,
                                            styling: { ...formData.styling, italic: !formData.styling.italic }
                                        })}
                                    >
                                        <span className="italic">I</span>
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Question</label>
                            <Input
                                value={formData.question}
                                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                                placeholder="e.g., How do I reset my password?"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Answer</label>
                            <Textarea
                                value={formData.answer}
                                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                                placeholder="Provide a detailed answer here..."
                                className={`${formData.styling.italic ? 'italic' : ''} ${formData.styling.bold ? 'font-bold' : ''}`}
                                rows={5}
                                required
                            />
                        </div>
                        <DialogFooter className="pt-4">
                            <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit">
                                {editingFaq ? 'Update FAQ' : 'Create FAQ'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={deleteDialog.open}
                onOpenChange={(open) => setDeleteDialog(prev => ({ ...prev, open }))}
                onConfirm={handleConfirmDelete}
                title="Delete FAQ"
                description="Are you sure you want to delete this FAQ? This action cannot be undone."
                confirmText="Delete FAQ"
                cancelText="Cancel"
                type="danger"
                isLoading={isDeleting}
                variant="minimal"
            />
        </div>
    );
}
